import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, ChevronDown, ChevronRight, Eye } from "lucide-react";
import {
  getPrompts,
  getPromptVersions,
  createPrompt,
  createPromptVersion,
  activatePromptVersion,
  deletePromptVersion,
  getProviders,
  getModelPricing,
  type AIPrompt,
} from "@/services/admin-api";
import type {
  CreatePromptInput,
  CreatePromptVersionInput,
} from "@/lib/schemas/ai-schemas";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Badge } from "@/components/ui/badge";
import { PromptFormDialog } from "@/components/ai/PromptFormDialog";
import { PromptVersionsTable } from "@/components/ai/PromptVersionsTable";
import { PromptPreviewDialog } from "@/components/ai/PromptPreviewDialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

interface PromptGroup {
  id: string;
  activeVersion: AIPrompt | undefined;
  latestVersion: AIPrompt;
  versions: AIPrompt[];
}

export default function AIPrompts() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [newVersionTarget, setNewVersionTarget] = useState<AIPrompt | null>(
    null,
  );
  const [expandedPrompts, setExpandedPrompts] = useState<Set<string>>(
    new Set(),
  );
  const [previewTarget, setPreviewTarget] = useState<string | null>(null);
  const [deleteVersionTarget, setDeleteVersionTarget] = useState<{
    promptId: string;
    version: number;
  } | null>(null);
  const [activatingVersion, setActivatingVersion] = useState<{
    promptId: string;
    version: number;
  } | null>(null);
  const [deletingVersion, setDeletingVersion] = useState<{
    promptId: string;
    version: number;
  } | null>(null);

  // Queries
  const { data: promptsData, isLoading } = useQuery({
    queryKey: ["ai-prompts"],
    queryFn: getPrompts,
  });

  const { data: providersData } = useQuery({
    queryKey: ["ai-providers"],
    queryFn: getProviders,
  });

  const { data: pricingData } = useQuery({
    queryKey: ["ai-pricing"],
    queryFn: getModelPricing,
  });

  const prompts = promptsData?.data ?? [];
  const providers = providersData?.data ?? [];
  const pricing = pricingData?.data ?? [];

  // Group prompts by ID
  const promptGroups: PromptGroup[] = Object.values(
    prompts.reduce(
      (acc, prompt) => {
        if (!acc[prompt.id]) {
          acc[prompt.id] = {
            id: prompt.id,
            activeVersion: undefined,
            latestVersion: prompt,
            versions: [],
          };
        }
        acc[prompt.id].versions.push(prompt);
        if (prompt.isActive) acc[prompt.id].activeVersion = prompt;
        if (prompt.version > acc[prompt.id].latestVersion.version) {
          acc[prompt.id].latestVersion = prompt;
        }
        return acc;
      },
      {} as Record<string, PromptGroup>,
    ),
  );

  // Expanded versions queries
  const versionsQueries = new Map<string, AIPrompt[]>();
  for (const id of expandedPrompts) {
    const group = promptGroups.find((g) => g.id === id);
    if (group) versionsQueries.set(id, group.versions);
  }

  // --- Create prompt ---
  const createMutation = useMutation({
    mutationFn: (data: CreatePromptInput) => createPrompt(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-prompts"] });
      toast({ title: "Prompt created" });
      setCreateOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create prompt",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // --- Create version ---
  const createVersionMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: CreatePromptVersionInput;
    }) => createPromptVersion(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-prompts"] });
      toast({ title: "New version created" });
      setNewVersionTarget(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create version",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // --- Activate version ---
  const handleActivateVersion = async (promptId: string, version: number) => {
    setActivatingVersion({ promptId, version });
    try {
      await activatePromptVersion(promptId, version);
      queryClient.invalidateQueries({ queryKey: ["ai-prompts"] });
      toast({ title: `Version ${version} activated` });
    } catch (error) {
      toast({
        title: "Failed to activate version",
        description: (error as Error).message,
        variant: "destructive",
      });
    } finally {
      setActivatingVersion(null);
    }
  };

  // --- Delete version ---
  const handleDeleteVersion = async () => {
    if (!deleteVersionTarget) return;
    const { promptId, version } = deleteVersionTarget;
    setDeletingVersion({ promptId, version });
    try {
      await deletePromptVersion(promptId, version);
      queryClient.invalidateQueries({ queryKey: ["ai-prompts"] });
      toast({ title: `Version ${version} deleted` });
    } catch (error) {
      toast({
        title: "Failed to delete version",
        description: (error as Error).message,
        variant: "destructive",
      });
    } finally {
      setDeletingVersion(null);
      setDeleteVersionTarget(null);
    }
  };

  // --- Fetch full versions when expanding ---
  const toggleExpand = async (promptId: string) => {
    const next = new Set(expandedPrompts);
    if (next.has(promptId)) {
      next.delete(promptId);
    } else {
      next.add(promptId);
      // Fetch all versions if we only have the active ones
      try {
        const response = await getPromptVersions(promptId);
        // Update the cache with full versions
        const group = promptGroups.find((g) => g.id === promptId);
        if (group && response.data.length > group.versions.length) {
          group.versions = response.data;
        }
        // Force re-render
        queryClient.setQueryData(["ai-prompt-versions", promptId], response);
      } catch {
        // Fall back to what we have
      }
    }
    setExpandedPrompts(next);
  };

  // Use fetched versions if available
  const getVersionsForPrompt = (promptId: string): AIPrompt[] => {
    const cached = queryClient.getQueryData<{ data: AIPrompt[] }>([
      "ai-prompt-versions",
      promptId,
    ]);
    if (cached) return cached.data;
    return promptGroups.find((g) => g.id === promptId)?.versions ?? [];
  };

  if (isLoading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="AI Prompts"
        description="Manage AI prompt configurations and versions"
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Prompt
          </Button>
        }
      />

      <div className="space-y-4">
        {promptGroups.map((group) => {
          const display = group.activeVersion || group.latestVersion;
          const isExpanded = expandedPrompts.has(group.id);

          return (
            <div key={group.id} className="bg-white rounded-lg shadow">
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">{group.id}</h3>
                    <div className="flex gap-4 mt-2 text-sm text-gray-600">
                      <span>Provider: {display.providerId}</span>
                      <span>Model: {display.model}</span>
                      {display.schemaRef && (
                        <span>Schema: {display.schemaRef}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right mr-4">
                      <div className="text-sm font-medium">
                        Active: <Badge variant={group.activeVersion ? "success" : "outline"}>v{group.activeVersion?.version ?? "None"}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {group.versions.length} version
                        {group.versions.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPreviewTarget(group.id)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setNewVersionTarget(display)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      New Version
                    </Button>
                  </div>
                </div>

                {/* System prompt preview */}
                <div className="bg-gray-50 p-4 rounded mt-4">
                  <div className="text-xs font-medium text-gray-500 mb-2">
                    SYSTEM PROMPT (v{display.version})
                  </div>
                  <div className="text-sm text-gray-900 whitespace-pre-wrap font-mono">
                    {display.systemPrompt.substring(0, 200)}
                    {display.systemPrompt.length > 200 && "..."}
                  </div>
                </div>

                {/* Expand/collapse versions */}
                <button
                  onClick={() => toggleExpand(group.id)}
                  className="mt-3 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  {isExpanded ? "Hide versions" : "Show all versions"}
                </button>

                {/* Versions table */}
                {isExpanded && (
                  <div className="mt-3 border rounded">
                    <PromptVersionsTable
                      versions={getVersionsForPrompt(group.id)}
                      onActivate={(version) =>
                        handleActivateVersion(group.id, version)
                      }
                      onDelete={(version) =>
                        setDeleteVersionTarget({ promptId: group.id, version })
                      }
                      activatingVersion={
                        activatingVersion?.promptId === group.id
                          ? activatingVersion.version
                          : null
                      }
                      deletingVersion={
                        deletingVersion?.promptId === group.id
                          ? deletingVersion.version
                          : null
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Prompt Dialog */}
      <PromptFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={(data) => createMutation.mutate(data as CreatePromptInput)}
        isPending={createMutation.isPending}
        providers={providers}
        pricing={pricing}
        mode="create"
      />

      {/* New Version Dialog */}
      <PromptFormDialog
        open={newVersionTarget !== null}
        onOpenChange={() => setNewVersionTarget(null)}
        onSubmit={(data) => {
          const versionData = Object.fromEntries(
            Object.entries(data as CreatePromptInput).filter(
              ([key]) => key !== "id",
            ),
          ) as CreatePromptVersionInput;
          createVersionMutation.mutate({
            id: newVersionTarget!.id,
            data: versionData,
          });
        }}
        isPending={createVersionMutation.isPending}
        providers={providers}
        pricing={pricing}
        mode="newVersion"
        prefill={newVersionTarget}
      />

      {/* Preview Dialog */}
      <PromptPreviewDialog
        open={previewTarget !== null}
        onOpenChange={() => setPreviewTarget(null)}
        promptId={previewTarget}
      />

      {/* Delete Version Confirm */}
      <ConfirmDialog
        open={deleteVersionTarget !== null}
        onOpenChange={() => setDeleteVersionTarget(null)}
        title="Delete Version"
        description={`Are you sure you want to delete version ${deleteVersionTarget?.version} of "${deleteVersionTarget?.promptId}"?`}
        onConfirm={handleDeleteVersion}
        loading={deletingVersion !== null}
      />
    </div>
  );
}
